// ===========================================
// Import / Export Page
// ===========================================
// Admin page for importing data from Simply Portal CSV exports
// and exporting school data as CSV or JSON.

import { useState, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Autocomplete,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Tab,
  Tabs,
  Switch,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormControl,
} from '@mui/material';
import {
  CloudUpload,
  Preview,
  PlayArrow,
  CheckCircle,
  Warning,
  InsertDriveFile,
  Close,
  Download,
} from '@mui/icons-material';
import Papa from 'papaparse';
import { useSnackbar } from 'notistack';
import PageHeader from '../../components/common/PageHeader';
import ImportProgressModal from '../../components/admin/import/ImportProgressModal';
import type { ModalPhase } from '../../components/admin/import/ImportProgressModal';
import { useTerms } from '../../hooks/useAdmin';
import { useImportPreview, useImportExecute } from '../../hooks/useImport';
import { useExportDownload } from '../../hooks/useExport';
import type { ImportPreviewResponse, ImportExecuteResponse } from '../../api/import.api';
import type { ExportEntityKey, ExportFormat } from '../../api/export.api';
import type { Term } from '../../api/admin.api';

// ===========================================
// TYPES
// ===========================================

interface FileInfo {
  file: File;
  rowCount: number;
  columns: string[];
}

// ===========================================
// EXPORT ENTITY DEFINITIONS
// ===========================================

interface ExportEntity {
  key: ExportEntityKey;
  label: string;
}

interface ExportGroup {
  label: string;
  entities: ExportEntity[];
}

const EXPORT_GROUPS: ExportGroup[] = [
  {
    label: 'People',
    entities: [
      { key: 'students', label: 'Students' },
      { key: 'families', label: 'Families' },
      { key: 'teachers', label: 'Teachers' },
    ],
  },
  {
    label: 'Scheduling',
    entities: [
      { key: 'lessons', label: 'Lessons' },
      { key: 'attendance', label: 'Attendance' },
      { key: 'terms', label: 'Terms' },
    ],
  },
  {
    label: 'Finance',
    entities: [
      { key: 'invoices', label: 'Invoices' },
      { key: 'payments', label: 'Payments' },
    ],
  },
  {
    label: 'Configuration',
    entities: [
      { key: 'instruments', label: 'Instruments' },
      { key: 'programLevels', label: 'Program Levels' },
      { key: 'locations', label: 'Locations' },
      { key: 'rooms', label: 'Rooms' },
      { key: 'lessonTypes', label: 'Lesson Types' },
      { key: 'lessonDurations', label: 'Lesson Durations' },
    ],
  },
];

const ALL_ENTITY_KEYS: ExportEntityKey[] = EXPORT_GROUPS.flatMap((g) =>
  g.entities.map((e) => e.key)
);

const DEFAULT_SELECTED: ExportEntityKey[] = ['students', 'families', 'teachers'];

// ===========================================
// DROP ZONE COMPONENT
// ===========================================

interface DropZoneProps {
  label: string;
  fileInfo: FileInfo | null;
  onFileSelected: (file: File) => void;
  onClear: () => void;
}

function DropZone({ label, fileInfo, onFileSelected, onClear }: DropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const isValidCsvFile = useCallback((file: File): boolean => {
    return file.type === 'text/csv' || file.name.endsWith('.csv');
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file && isValidCsvFile(file)) {
        onFileSelected(file);
      }
    },
    [onFileSelected, isValidCsvFile]
  );

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file && isValidCsvFile(file)) {
        onFileSelected(file);
      }
      // Reset input so the same file can be re-selected
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [onFileSelected, isValidCsvFile]
  );

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      inputRef.current?.click();
    }
  }, []);

  if (fileInfo) {
    return (
      <Box
        sx={{
          border: '2px solid',
          borderColor: 'success.main',
          borderRadius: 2,
          p: 2,
          bgcolor: 'success.light',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <InsertDriveFile color="success" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2">{label}</Typography>
          <Typography variant="body2" color="text.secondary">
            {fileInfo.file.name} - {fileInfo.rowCount} rows, {fileInfo.columns.length} columns
          </Typography>
        </Box>
        <Button size="small" onClick={onClear} startIcon={<Close />}>
          Remove
        </Button>
      </Box>
    );
  }

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`Upload ${label} file`}
      onDrop={handleDrop}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={() => inputRef.current?.click()}
      onKeyDown={handleKeyDown}
      sx={{
        border: '2px dashed',
        borderColor: isDragOver ? 'primary.main' : 'divider',
        borderRadius: 2,
        p: 3,
        textAlign: 'center',
        cursor: 'pointer',
        bgcolor: isDragOver ? 'action.hover' : 'transparent',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: 'primary.light',
          bgcolor: 'action.hover',
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".csv"
        onChange={handleFileInput}
        style={{ display: 'none' }}
      />
      <CloudUpload sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
      <Typography variant="subtitle2">{label}</Typography>
      <Typography variant="body2" color="text.secondary">
        Drag & drop a CSV file here, or click to browse
      </Typography>
    </Box>
  );
}

// ===========================================
// RESULT SUMMARY COMPONENT
// ===========================================

interface ResultRowProps {
  label: string;
  created: number;
  existing: number;
}

function ResultRow({ label, created, existing }: ResultRowProps) {
  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell align="center">
        {created > 0 ? (
          <Chip label={created} color="success" size="small" />
        ) : (
          <Typography variant="body2" color="text.secondary">0</Typography>
        )}
      </TableCell>
      <TableCell align="center">
        {existing > 0 ? (
          <Chip label={existing} color="default" size="small" />
        ) : (
          <Typography variant="body2" color="text.secondary">0</Typography>
        )}
      </TableCell>
    </TableRow>
  );
}

// ===========================================
// DAY OF WEEK HELPER
// ===========================================

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// ===========================================
// IMPORT TAB CONTENT
// ===========================================

function ImportTabContent() {
  const { data: termsData } = useTerms();
  const terms: Term[] = Array.isArray(termsData) ? termsData : (termsData as unknown as { data?: Term[] })?.data ?? [];

  const [selectedTerm, setSelectedTerm] = useState<Term | null>(null);
  const [classesFileInfo, setClassesFileInfo] = useState<FileInfo | null>(null);
  const [studentsFileInfo, setStudentsFileInfo] = useState<FileInfo | null>(null);
  const [enrollmentsFileInfo, setEnrollmentsFileInfo] = useState<FileInfo | null>(null);
  const [previewData, setPreviewData] = useState<ImportPreviewResponse['data'] | null>(null);
  const [importResult, setImportResult] = useState<ImportExecuteResponse['data'] | null>(null);

  // Progress modal state
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [modalPhase, setModalPhase] = useState<ModalPhase>('importing');
  const [modalError, setModalError] = useState<string | undefined>();

  const previewMutation = useImportPreview();
  const executeMutation = useImportExecute();

  // Parse CSV client-side for quick file info
  const handleFileSelected = useCallback(
    (type: 'classes' | 'students' | 'enrollments') => (file: File) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const info: FileInfo = {
            file,
            rowCount: results.data.length,
            columns: results.meta.fields || [],
          };
          if (type === 'classes') {
            setClassesFileInfo(info);
          } else if (type === 'students') {
            setStudentsFileInfo(info);
          } else {
            setEnrollmentsFileInfo(info);
          }
          // Clear previous preview/results when files change
          setPreviewData(null);
          setImportResult(null);
        },
      });
    },
    []
  );

  const handlePreview = useCallback(() => {
    if (!classesFileInfo || !studentsFileInfo) return;

    previewMutation.mutate(
      {
        classesFile: classesFileInfo.file,
        studentsFile: studentsFileInfo.file,
        enrollmentsFile: enrollmentsFileInfo?.file,
      },
      {
        onSuccess: (response) => {
          setPreviewData(response.data);
          setImportResult(null);
        },
      }
    );
  }, [classesFileInfo, studentsFileInfo, enrollmentsFileInfo, previewMutation]);

  const handleExecute = useCallback(() => {
    if (!classesFileInfo || !studentsFileInfo || !selectedTerm) return;
    if (progressModalOpen) return; // prevent double-trigger

    // Open modal in importing state
    setModalPhase('importing');
    setModalError(undefined);
    setProgressModalOpen(true);

    executeMutation.mutate(
      {
        classesFile: classesFileInfo.file,
        studentsFile: studentsFileInfo.file,
        termId: selectedTerm.id,
        enrollmentsFile: enrollmentsFileInfo?.file,
      },
      {
        onSuccess: (response) => {
          setImportResult(response.data);
          setModalPhase('success');
        },
        onError: (error) => {
          setModalError(error?.message || 'An unexpected error occurred during import.');
          setModalPhase('error');
        },
      }
    );
  }, [classesFileInfo, studentsFileInfo, enrollmentsFileInfo, selectedTerm, executeMutation, progressModalOpen]);

  const canPreview = classesFileInfo && studentsFileInfo;
  const canExecute = canPreview && selectedTerm && previewData;

  return (
    <>
      {/* Import from Simply Portal */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Import from Simply Portal
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Upload Classes and Student Details CSV files exported from Simply Portal to import
            locations, rooms, instruments, program levels, teachers, families, students, and lessons.
            Optionally include an Enrollments CSV for precise student-to-class matching.
          </Typography>

          {/* Term Selector */}
          <Autocomplete
            options={terms}
            getOptionLabel={(term) => term.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedTerm}
            onChange={(_, newValue) => setSelectedTerm(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Term"
                placeholder="Choose a term for imported lessons"
                helperText="All imported lessons will be assigned to this term"
              />
            )}
            sx={{ mb: 3, maxWidth: 400 }}
          />

          {/* Drop Zones */}
          <Stack spacing={2} sx={{ mb: 3 }}>
            <DropZone
              label="Classes CSV"
              fileInfo={classesFileInfo}
              onFileSelected={handleFileSelected('classes')}
              onClear={() => {
                setClassesFileInfo(null);
                setPreviewData(null);
                setImportResult(null);
              }}
            />
            <DropZone
              label="Student Details CSV"
              fileInfo={studentsFileInfo}
              onFileSelected={handleFileSelected('students')}
              onClear={() => {
                setStudentsFileInfo(null);
                setPreviewData(null);
                setImportResult(null);
              }}
            />
            <DropZone
              label="Enrolments CSV (Optional)"
              fileInfo={enrollmentsFileInfo}
              onFileSelected={handleFileSelected('enrollments')}
              onClear={() => {
                setEnrollmentsFileInfo(null);
                setPreviewData(null);
                setImportResult(null);
              }}
            />
          </Stack>

          {/* Preview Button */}
          <Button
            variant="outlined"
            startIcon={
              previewMutation.isPending ? (
                <CircularProgress size={20} />
              ) : (
                <Preview />
              )
            }
            onClick={handlePreview}
            disabled={!canPreview || previewMutation.isPending}
            sx={{ mr: 2 }}
          >
            {previewMutation.isPending ? 'Parsing...' : 'Preview Import'}
          </Button>

          {/* Execute Button */}
          {previewData && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<PlayArrow />}
              onClick={handleExecute}
              disabled={!canExecute || executeMutation.isPending}
            >
              Execute Import
            </Button>
          )}

          {/* Error Messages */}
          {previewMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Preview failed: {previewMutation.error?.message || 'Unknown error'}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Preview Results */}
      {previewData && !importResult && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Import Preview
            </Typography>

            {/* Summary Counts */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: 2,
                mb: 3,
              }}
            >
              {Object.entries(previewData.counts)
                .filter(([key]) => key !== 'enrollments')
                .map(([key, value]) => (
                <Paper key={key} sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h5" color="primary.main">
                    {value as number}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </Typography>
                </Paper>
              ))}
              {previewData.counts.enrollments && (
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h5" color="success.main">
                    {previewData.counts.enrollments.matched}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Enrollments
                  </Typography>
                  {(previewData.counts.enrollments.ambiguous > 0 || previewData.counts.enrollments.noMatch > 0) && (
                    <Box sx={{ mt: 0.5 }}>
                      {previewData.counts.enrollments.ambiguous > 0 && (
                        <Chip label={`${previewData.counts.enrollments.ambiguous} ambiguous`} color="warning" size="small" sx={{ mr: 0.5 }} />
                      )}
                      {previewData.counts.enrollments.noMatch > 0 && (
                        <Chip label={`${previewData.counts.enrollments.noMatch} unmatched`} color="error" size="small" />
                      )}
                    </Box>
                  )}
                </Paper>
              )}
            </Box>

            {/* Warnings */}
            {previewData.warnings.length > 0 && (
              <Alert severity="warning" sx={{ mb: 3 }} icon={<Warning />}>
                <Typography variant="subtitle2" gutterBottom>
                  Data Quality Warnings ({previewData.warnings.length})
                </Typography>
                {previewData.warnings.map((w, i) => (
                  <Typography key={i} variant="body2" sx={{ ml: 1 }}>
                    {w}
                  </Typography>
                ))}
              </Alert>
            )}

            {!selectedTerm && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Select a term above before executing the import.
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Lookups Summary */}
            <Typography variant="subtitle2" gutterBottom>
              Entities to Create/Match
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {previewData.lookups.teachers.map((t) => (
                <Chip key={`t-${t}`} label={t} size="small" color="primary" variant="outlined" />
              ))}
              {previewData.lookups.locations.map((l) => (
                <Chip key={`l-${l}`} label={l} size="small" color="secondary" variant="outlined" />
              ))}
              {previewData.lookups.rooms.map((r) => (
                <Chip key={`r-${r}`} label={r} size="small" variant="outlined" />
              ))}
              {previewData.lookups.instruments.map((i) => (
                <Chip key={`i-${i}`} label={i} size="small" color="info" variant="outlined" />
              ))}
              {previewData.lookups.programLevels.map((p) => (
                <Chip key={`p-${p}`} label={p} size="small" color="success" variant="outlined" />
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Classes Preview Table */}
            <Typography variant="subtitle2" gutterBottom>
              Classes (first 10)
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Day</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Teacher</TableCell>
                    <TableCell>Room</TableCell>
                    <TableCell>Location</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.parsedClasses.slice(0, 10).map((cls, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{cls.className}</TableCell>
                      <TableCell>{DAYS[cls.dayOfWeek]}</TableCell>
                      <TableCell>{cls.startTime} - {cls.endTime}</TableCell>
                      <TableCell>{cls.durationMins}m</TableCell>
                      <TableCell>
                        <Chip
                          label={cls.lessonType}
                          size="small"
                          color={cls.lessonType === 'INDIVIDUAL' ? 'info' : 'success'}
                        />
                      </TableCell>
                      <TableCell>{cls.teacherName}</TableCell>
                      <TableCell>{cls.roomName}</TableCell>
                      <TableCell>{cls.locationName}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Students Preview Table */}
            <Typography variant="subtitle2" gutterBottom>
              Students (first 10)
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>DOB</TableCell>
                    <TableCell>Instrument</TableCell>
                    <TableCell>Program Level</TableCell>
                    <TableCell>Family</TableCell>
                    <TableCell>Email</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.parsedStudents.slice(0, 10).map((s, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        {s.firstName} {s.lastName}
                      </TableCell>
                      <TableCell>
                        {s.birthDate
                          ? new Date(s.birthDate).toLocaleDateString('en-AU')
                          : '-'}
                        {s.birthDateWarning && (
                          <Warning sx={{ fontSize: 14, ml: 0.5, color: 'warning.main', verticalAlign: 'middle' }} />
                        )}
                      </TableCell>
                      <TableCell>{s.instrument || '-'}</TableCell>
                      <TableCell>{s.programLevel || '-'}</TableCell>
                      <TableCell>{s.familyName}</TableCell>
                      <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {s.email || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Enrollment Preview Table */}
            {previewData.enrollmentPreview && previewData.enrollmentPreview.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Enrollment Matching Preview (first 20)
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Student</TableCell>
                        <TableCell>Matched Class</TableCell>
                        <TableCell align="center">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewData.enrollmentPreview.slice(0, 20).map((ep, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{ep.studentName}</TableCell>
                          <TableCell>
                            {ep.status === 'matched' && ep.className}
                            {ep.status === 'ambiguous' && (
                              <Typography variant="body2" color="text.secondary">
                                {ep.candidates?.join(' / ')}
                              </Typography>
                            )}
                            {ep.status === 'no_match' && (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={ep.status === 'matched' ? 'Matched' : ep.status === 'ambiguous' ? 'Ambiguous' : 'No Match'}
                              size="small"
                              color={ep.status === 'matched' ? 'success' : ep.status === 'ambiguous' ? 'warning' : 'error'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {previewData.enrollmentPreview.length > 20 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Showing 20 of {previewData.enrollmentPreview.length} enrollment matches
                  </Typography>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Import Results */}
      {importResult && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              {importResult.success ? (
                <CheckCircle color="success" />
              ) : (
                <Warning color="error" />
              )}
              <Typography variant="h6">
                {importResult.success ? 'Import Complete' : 'Import Completed with Errors'}
              </Typography>
            </Box>

            {importResult.errors.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {importResult.errors.map((err, i) => (
                  <Typography key={i} variant="body2">
                    {err}
                  </Typography>
                ))}
              </Alert>
            )}

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Entity</TableCell>
                    <TableCell align="center">Created</TableCell>
                    <TableCell align="center">Already Existed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <ResultRow label="Locations" {...importResult.locations} />
                  <ResultRow label="Rooms" {...importResult.rooms} />
                  <ResultRow label="Instruments" {...importResult.instruments} />
                  <ResultRow label="Program Levels" {...importResult.programLevels} />
                  <ResultRow label="Lesson Durations" {...importResult.lessonDurations} />
                  <ResultRow label="Lesson Types" {...importResult.lessonTypes} />
                  <ResultRow label="Teachers" {...importResult.teachers} />
                  <ResultRow label="Families" {...importResult.families} />
                  <ResultRow label="Parents" {...importResult.parents} />
                  <ResultRow label="Students" {...importResult.students} />
                  <ResultRow label="Lessons" {...importResult.lessons} />
                  <ResultRow label="Enrolments" {...importResult.enrollments} />
                  {importResult.enrollments?.ambiguous > 0 && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Chip
                          label={`${importResult.enrollments.ambiguous} ambiguous (need manual assignment)`}
                          color="warning"
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Import Progress Modal */}
      <ImportProgressModal
        open={progressModalOpen}
        phase={modalPhase}
        errorMessage={modalError}
        onClose={() => setProgressModalOpen(false)}
      />
    </>
  );
}

// ===========================================
// EXPORT TAB CONTENT
// ===========================================

function ExportTabContent() {
  const [selectedEntities, setSelectedEntities] = useState<Record<ExportEntityKey, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const key of ALL_ENTITY_KEYS) {
      initial[key] = DEFAULT_SELECTED.includes(key);
    }
    return initial as Record<ExportEntityKey, boolean>;
  });
  const [exportFormat, setExportFormat] = useState<ExportFormat>('csv');

  const { enqueueSnackbar } = useSnackbar();
  const exportMutation = useExportDownload();

  const selectedCount = useMemo(
    () => ALL_ENTITY_KEYS.filter((k) => selectedEntities[k]).length,
    [selectedEntities]
  );

  const allSelected = selectedCount === ALL_ENTITY_KEYS.length;
  const someSelected = selectedCount > 0 && !allSelected;

  const handleToggleAll = useCallback(() => {
    const newValue = !allSelected;
    const updated: Record<string, boolean> = {};
    for (const key of ALL_ENTITY_KEYS) {
      updated[key] = newValue;
    }
    setSelectedEntities(updated as Record<ExportEntityKey, boolean>);
  }, [allSelected]);

  const handleToggleEntity = useCallback((key: ExportEntityKey) => {
    setSelectedEntities((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleDownload = useCallback(() => {
    const entities = ALL_ENTITY_KEYS.filter((k) => selectedEntities[k]);
    if (entities.length === 0) return;
    exportMutation.mutate(
      { entities, format: exportFormat },
      {
        onSuccess: () => {
          enqueueSnackbar('Export downloaded successfully', { variant: 'success' });
        },
      }
    );
  }, [selectedEntities, exportFormat, exportMutation, enqueueSnackbar]);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Export School Data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Select data to export, choose a format, and download.
        </Typography>

        {/* Select All */}
        <FormControlLabel
          control={
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={handleToggleAll}
            />
          }
          label={
            <Typography variant="subtitle2">
              Select All ({selectedCount}/{ALL_ENTITY_KEYS.length})
            </Typography>
          }
          sx={{ mb: 1 }}
        />

        <Divider sx={{ mb: 2 }} />

        {/* Entity Groups */}
        {EXPORT_GROUPS.map((group) => (
          <Box key={group.label} sx={{ mb: 2 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: 'block', mb: 0.5 }}
            >
              {group.label}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {group.entities.map((entity) => (
                <FormControlLabel
                  key={entity.key}
                  control={
                    <Switch
                      size="small"
                      checked={selectedEntities[entity.key]}
                      onChange={() => handleToggleEntity(entity.key)}
                    />
                  }
                  label={<Typography variant="body2">{entity.label}</Typography>}
                  sx={{ minWidth: 160, mr: 2 }}
                />
              ))}
            </Box>
          </Box>
        ))}

        <Divider sx={{ my: 2 }} />

        {/* Format Selector */}
        <FormControl sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Format
          </Typography>
          <RadioGroup
            row
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
          >
            <FormControlLabel value="csv" control={<Radio />} label="CSV" />
            <FormControlLabel value="json" control={<Radio />} label="JSON" />
          </RadioGroup>
        </FormControl>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 3 }}>
          {exportFormat === 'csv'
            ? 'CSV: one file per entity (ZIP if multiple selected).'
            : 'JSON: all selected data in a single file.'}
        </Typography>

        {/* Download Button */}
        <Button
          variant="contained"
          startIcon={
            exportMutation.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Download />
            )
          }
          onClick={handleDownload}
          disabled={selectedCount === 0 || exportMutation.isPending}
        >
          {exportMutation.isPending ? 'Preparing...' : 'Download Export'}
        </Button>

        {/* Error */}
        {exportMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Export failed: {exportMutation.error?.message || 'Unknown error'}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function ImportExportPage() {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box>
      <PageHeader
        title="Import / Export"
        subtitle="Import data from Simply Portal or export school data"
      />

      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{ mb: 3 }}
      >
        <Tab label="Import" />
        <Tab label="Export" />
      </Tabs>

      {activeTab === 0 && <ImportTabContent />}
      {activeTab === 1 && <ExportTabContent />}
    </Box>
  );
}
